import { createRoomWidgetClient, EventType, type MatrixClient } from 'matrix-js-sdk/src';
import { MatrixCapabilities, WidgetApi } from 'matrix-widget-api';

import logger from 'utils/logger';
import { CustomMeedioMatrixEvents } from 'utils/matrixUtils';
import { getUrlParams } from 'widget/utils/UrlParams';

export interface JoinCallData {
  audioInput: string | null;
  videoInput: string | null;
}

export interface WidgetHelpers {
  api: WidgetApi;
  client: MatrixClient;
}

export const getWidgetClient = async () => {
  try {
    const parsedCfg = getUrlParams();
    if (!parsedCfg) throw new Error('Missing required widget parameters');

    const { deviceId, userId, accessToken, baseUrl, roomId } = parsedCfg;
    if (!deviceId || !userId || !accessToken || !baseUrl || !roomId) {
      throw new Error('Missing required widget parameters');
    }

    const api = new WidgetApi(parsedCfg.widgetId, parsedCfg.parentUrl);

    api.requestCapabilities(['org.matrix.msc2931.get_client_wellknown']);
    api.requestCapability(MatrixCapabilities.AlwaysOnScreen);
    api.requestCapability('org.matrix.msc2967.openid_credentials');

    const sendEvent = ['m.call', EventType.GroupCallMemberPrefix];
    const sendRecvEvent = [
      'org.matrix.rageshake_request',
      CustomMeedioMatrixEvents.REACTION,
      EventType.CallEncryptionKeysPrefix,
      EventType.Reaction,
      EventType.RoomRedaction,
    ];

    api.on('action:theme_change', () => null);

    const sendState = [{ eventType: EventType.GroupCallMemberPrefix }];
    const receiveState = [
      { eventType: EventType.RoomCreate },
      { eventType: EventType.RoomName },
      { eventType: EventType.RoomMember },
      { eventType: EventType.RoomEncryption },
      { eventType: EventType.GroupCallMemberPrefix },
      { eventType: EventType.RoomJoinRules },
    ];

    const sendRecvToDevice = [
      EventType.CallInvite,
      EventType.CallCandidates,
      EventType.CallAnswer,
      EventType.CallHangup,
      EventType.CallReject,
      EventType.CallSelectAnswer,
      EventType.CallNegotiate,
      EventType.CallSDPStreamMetadataChanged,
      EventType.CallSDPStreamMetadataChangedPrefix,
      EventType.CallReplaces,
      EventType.CallEncryptionKeysPrefix,
    ];

    const mx: MatrixClient = createRoomWidgetClient(
      // TypeScript is being difficult here
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      api,
      {
        sendEvent: [...sendEvent, ...sendRecvEvent],
        receiveEvent: sendRecvEvent,
        sendState,
        receiveState,
        sendToDevice: sendRecvToDevice,
        receiveToDevice: sendRecvToDevice,
        sendDelayedEvents: true,
        updateDelayedEvents: true,
      },
      roomId,
      {
        baseUrl,
        timelineSupport: true,
        userId,
        deviceId,
        accessToken,
        useLivekitForGroupCalls: true,
      },
      true
    );

    await mx.startClient({ clientWellKnownPollPeriod: 60 * 10, lazyLoadMembers: true });

    return { api, client: mx };
  } catch (e) {
    logger.error('Error setting up widget client', e);
    throw e;
  }
};
